import { useEffect, useMemo, useState } from "react";
import { Clock, PlayCircle, StopCircle, Calendar, PaperPlaneTilt, CheckCircle, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuthGate } from "@/lib/hooks/useAuthGate";
import SessionExpiredState from "@/components/auth/SessionExpiredState";
import {
  getOpenEntry,
  clockIn,
  clockOut,
  listMyEntries,
  startOfWeek,
  endOfWeek,
  formatDuration,
  computeEntryMinutes,
  getStaffTimecardSnapshot,
  submitStaffTimecard,
  localDateKey,
  timecardStatusLabel,
} from "@/lib/scheduling";

/**
 * Teacher · /teacher/timesheet
 *
 * Big clock-in / clock-out button, running timer, and a list of this
 * week's entries with total minutes. Optional break minutes + notes on
 * clock-out. No classroom picker in v1 — teachers get auto-assigned
 * based on their active staff_assignment.
 */
export default function TeacherTimesheet() {
  const { status, profile } = useAuthGate();
  const [openEntry, setOpenEntry] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false); // clock-out form
  const [breakMin, setBreakMin] = useState("0");
  const [outNotes, setOutNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [timecard, setTimecard] = useState(null);
  const [submittingTimecard, setSubmittingTimecard] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Tick once per minute so the running timer updates.
  useEffect(() => {
    if (!openEntry) return;
    const id = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, [openEntry]);

  async function refresh() {
    if (status !== "ready") {
      if (status === "session_expired") setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const weekStart = startOfWeek();
      const [o, rows, snapshot] = await Promise.all([
        getOpenEntry(profile.id),
        listMyEntries(profile.id, {
          from: weekStart.toISOString(),
          to: endOfWeek().toISOString(),
          limit: 50,
        }),
        // During a rolling deploy, the existing clock surface remains useful
        // even if the newly added review RPC has not reached this environment.
        getStaffTimecardSnapshot({ weekStart: localDateKey(weekStart) }).catch(() => null),
      ]);
      setOpenEntry(o);
      setEntries(rows);
      setTimecard(snapshot);
    } catch (err) {
      toast.error(err?.message || "Could not load timesheet");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, profile?.id]);

  const totalMinutes = useMemo(() => {
    let total = 0;
    for (const e of entries) total += computeEntryMinutes(e, new Date(now));
    return total;
  }, [entries, now]);

  const runningMinutes = useMemo(
    () => (openEntry ? computeEntryMinutes(openEntry, new Date(now)) : 0),
    [openEntry, now],
  );

  async function handleClockIn() {
    setSaving(true);
    try {
      await clockIn();
      await refresh();
      toast.success("Clocked in");
    } catch (err) {
      toast.error(err?.message || "Could not clock in");
    } finally {
      setSaving(false);
    }
  }

  async function handleClockOut() {
    setSaving(true);
    try {
      await clockOut({
        breakMinutes: Number(breakMin) || 0,
        notes: outNotes.trim() || null,
      });
      setSheetOpen(false);
      setBreakMin("0");
      setOutNotes("");
      await refresh();
      toast.success("Clocked out");
    } catch (err) {
      toast.error(err?.message || "Could not clock out");
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmitTimecard() {
    if (!timecard?.summary?.maySubmit || openEntry) {
      toast.error(openEntry ? "Clock out before submitting your timecard." : "Add a completed entry before submitting.");
      return;
    }
    setSubmittingTimecard(true);
    try {
      const snapshot = await submitStaffTimecard({ weekStart: timecard.weekStart });
      setTimecard(snapshot);
      toast.success("Timecard submitted for review");
    } catch (err) {
      toast.error(err?.message || "Could not submit timecard");
    } finally {
      setSubmittingTimecard(false);
    }
  }

  if (status === "session_expired") {
    return <SessionExpiredState scope="timesheet" testId="teacher-timesheet-session-expired" />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="teacher-timesheet" className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Staff
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Your timesheet
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Clock in when your shift starts, out when it ends. Breaks are
          optional; add them on the way out.
        </p>
      </header>

      {/* Status card */}
      <section
        data-testid="teacher-timesheet-status-card"
        data-state={openEntry ? "clocked-in" : "clocked-out"}
        className={`rounded-2xl border p-6 ${
          openEntry ? "border-primary/40 bg-primary/5" : "border-neutral-200 bg-white"
        }`}
      >
        <div className="flex items-center gap-4">
          <div
            className={`flex h-14 w-14 items-center justify-center rounded-full ${
              openEntry ? "bg-primary/15 text-primary-hover" : "bg-neutral-100 text-neutral-500"
            }`}
          >
            <Clock size={26} weight="duotone" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
              {openEntry ? "Clocked in" : "Not clocked in"}
            </p>
            {openEntry ? (
              <>
                <p
                  data-testid="teacher-timesheet-running-minutes"
                  className="font-heading text-2xl font-semibold text-neutral-900"
                >
                  {formatDuration(runningMinutes)}
                </p>
                <p className="text-xs text-neutral-500">
                  Since {new Date(openEntry.clock_in).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </p>
              </>
            ) : (
              <p className="font-heading text-xl font-semibold text-neutral-900">
                Ready when you are.
              </p>
            )}
          </div>
        </div>
        <div className="mt-4">
          {openEntry ? (
            <button
              type="button"
              data-testid="teacher-timesheet-clock-out-open"
              disabled={saving}
              onClick={() => setSheetOpen(true)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C6851E] px-4 py-3 font-semibold text-white hover:bg-[#8A5A12] disabled:opacity-60"
            >
              <StopCircle size={18} weight="duotone" />
              Clock out
            </button>
          ) : (
            <button
              type="button"
              data-testid="teacher-timesheet-clock-in"
              disabled={saving}
              onClick={handleClockIn}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 font-semibold text-white hover:bg-primary-hover disabled:opacity-60"
            >
              <PlayCircle size={18} weight="duotone" />
              Clock in
            </button>
          )}
        </div>
      </section>

      {/* Week total */}
      <section
        data-testid="teacher-timesheet-week-total"
        className="rounded-2xl border border-neutral-200 bg-white p-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar size={16} weight="duotone" className="text-primary-hover" />
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              This week
            </p>
          </div>
          <p className="font-heading text-lg font-semibold text-neutral-900">
            {formatDuration(totalMinutes)}
          </p>
        </div>
      </section>

      {timecard && (
        <TeacherTimecardReview
          snapshot={timecard}
          hasOpenEntry={!!openEntry}
          saving={submittingTimecard}
          onSubmit={handleSubmitTimecard}
        />
      )}

      {/* Entries list */}
      <section>
        <h2 className="mb-3 font-heading text-base font-semibold text-neutral-900">
          Recent entries
        </h2>
        {entries.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-6 text-center text-sm text-neutral-500">
            No entries this week yet.
          </p>
        ) : (
          <ul data-testid="teacher-timesheet-entries" className="space-y-2">
            {entries.map((e) => {
              const d = new Date(e.clock_in);
              const day = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
              const inStr = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
              const outStr = e.clock_out
                ? new Date(e.clock_out).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                : "—";
              const mins = computeEntryMinutes(e, new Date(now));
              const isOpen = !e.clock_out;
              return (
                <li
                  key={e.id}
                  data-testid={`teacher-timesheet-entry-${e.id}`}
                  data-open={isOpen}
                  className="flex items-start justify-between gap-3 rounded-2xl border border-neutral-200 bg-white p-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-heading text-sm font-semibold text-neutral-900">
                      {day}
                      {isOpen && (
                        <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-hover">
                          Open
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {inStr} → {outStr}
                      {e.break_minutes > 0 && (
                        <span> · {e.break_minutes}m break</span>
                      )}
                    </p>
                    {e.notes && (
                      <p className="mt-1 text-xs text-neutral-600">"{e.notes}"</p>
                    )}
                  </div>
                  <p className="shrink-0 font-heading text-sm font-semibold text-neutral-900">
                    {formatDuration(mins)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Clock-out sheet */}
      {sheetOpen && (
        <div
          data-testid="teacher-timesheet-clock-out-sheet"
          className="fixed inset-0 z-50 flex items-end bg-black/40 sm:items-center sm:justify-center"
          onClick={() => !saving && setSheetOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-t-3xl bg-white p-5 sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-3 font-heading text-lg font-semibold text-neutral-900">
              Clock out
            </h2>
            <label className="block">
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">
                Break (minutes)
              </span>
              <input
                type="number"
                min={0}
                max={599}
                data-testid="teacher-timesheet-break-input"
                value={breakMin}
                onChange={(e) => setBreakMin(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </label>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">
                Notes (optional)
              </span>
              <textarea
                rows={2}
                data-testid="teacher-timesheet-notes-input"
                value={outNotes}
                onChange={(e) => setOutNotes(e.target.value)}
                placeholder="Anything worth noting?"
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                disabled={saving}
                className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="teacher-timesheet-clock-out-confirm"
                disabled={saving}
                onClick={handleClockOut}
                className="rounded-full bg-[#C6851E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#8A5A12] disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save & clock out"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TeacherTimecardReview({ snapshot, hasOpenEntry, saving, onSubmit }) {
  const card = snapshot.timecard || {};
  const summary = snapshot.summary || {};
  const status = card.status || "open";
  const maySubmit = !!summary.maySubmit && !hasOpenEntry;
  const tone = {
    open: "border-neutral-200 bg-white",
    submitted: "border-primary/30 bg-primary/5",
    approved: "border-primary/30 bg-primary/5",
    reopened: "border-[#C6851E]/40 bg-[#C6851E]/5",
  }[status] || "border-neutral-200 bg-white";

  return (
    <section
      data-testid="teacher-timesheet-timecard-review"
      data-status={status}
      className={`rounded-2xl border p-4 ${tone}`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
          status === "approved" || status === "submitted" ? "bg-primary/15 text-primary-hover" : "bg-neutral-100 text-neutral-600"
        }`}>
          {status === "approved" ? <CheckCircle size={20} weight="duotone" /> : <PaperPlaneTilt size={20} weight="duotone" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-base font-semibold text-neutral-900">Timecard review</h2>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
              status === "approved" || status === "submitted"
                ? "bg-primary/15 text-primary-hover"
                : status === "reopened"
                  ? "bg-[#C6851E]/15 text-[#8A5A12]"
                  : "bg-neutral-100 text-neutral-600"
            }`}>
              {timecardStatusLabel(status)}
            </span>
          </div>

          {status === "approved" ? (
            <>
              <p className="mt-1 text-sm text-neutral-700">
                Approved{card.approvedAt ? ` ${new Date(card.approvedAt).toLocaleDateString()}` : ""}.
              </p>
              {card.approvalNote && <p className="mt-1 text-xs text-neutral-600">“{card.approvalNote}”</p>}
            </>
          ) : status === "submitted" ? (
            <p className="mt-1 text-sm text-neutral-700">
              Submitted{card.submittedAt ? ` ${new Date(card.submittedAt).toLocaleDateString()}` : ""}. Your manager can approve it or send it back for review.
            </p>
          ) : status === "reopened" ? (
            <>
              <p className="mt-1 text-sm text-neutral-700">
                Your manager asked you to review this week before resubmitting.
              </p>
              {card.reopenReason && (
                <p className="mt-2 rounded-xl bg-white/80 p-2 text-xs text-neutral-700">
                  <ArrowCounterClockwise className="mr-1 inline text-[#8A5A12]" size={14} weight="duotone" />
                  {card.reopenReason}
                </p>
              )}
              {summary.correctionCount > 0 && (
                <p className="mt-2 text-xs text-neutral-600">
                  {summary.correctionCount} manager {summary.correctionCount === 1 ? "correction was" : "corrections were"} recorded. Review your entries before resubmitting.
                </p>
              )}
            </>
          ) : (
            <p className="mt-1 text-sm text-neutral-600">
              When this week is ready, submit your completed entries for manager review.
            </p>
          )}
        </div>
      </div>

      {(status === "open" || status === "reopened") && (
        <div className="mt-4">
          <button
            type="button"
            data-testid="teacher-timesheet-submit-timecard"
            disabled={!maySubmit || saving}
            onClick={onSubmit}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PaperPlaneTilt size={16} weight="duotone" />
            {saving ? "Submitting…" : status === "reopened" ? "Resubmit timecard" : "Submit timecard"}
          </button>
          {!maySubmit && (
            <p className="mt-2 text-center text-xs text-neutral-500">
              {hasOpenEntry
                ? "Clock out before submitting this week."
                : summary.entryCount > 0
                  ? "Review the entries that still need attention before submitting."
                  : "Add a completed time entry before submitting."}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
